import React, { useMemo, useState } from "react";
import {
  DetailsListLayoutMode,
  IColumn,
  IDetailsHeaderProps,
  mergeStyles,
  ScrollablePane,
  ScrollbarVisibility,
  SelectionMode,
  ShimmeredDetailsList,
  Stack,
  Sticky,
  StickyPositionType,
  Text,
} from "@fluentui/react";
import { getExperimentsTableColumns } from "./experimentsTableColumnDefinitions";
import { useFetchEv2RunStatuses } from "../Hooks/AdoHooks/UseFetchEv2RunStatuses";
import ExperimentsTableNoExperiments from "./ExperimentsTableNoExperiments";
import NoEV2Experiments from "../NoEV2Experiments/NoEV2Experiments";
import { styles } from "./experimentsTable.styles";
import { styles as sharedStyles } from "../Shared/shared.styles";
import { ExperimentType } from "../Types";
import { useEv2ExperimentParams } from "../Hooks/UseCurrentExtension";
import { useNavigateEv2Experiments } from "../Hooks/UseNavigateToEv2Experiments";
import { useAppContext } from "../Contexts";
import FilterComponent from "../Shared/FilterComponent/FilterComponent";
import {
  customDurationFilter,
  customLastModifiedDateFilter,
  customStatusFilter,
  getFilters,
} from "./experimentTableFilters";
import { useTableFiltering } from "../Hooks/useTableFiltering";
import { useTableSorting } from "../Hooks/useTableSorting";
import { createColumnClickHandler } from "../Shared/shared.utils";
import ExperimentTableSelectExtension from "./ExperimentTableSelectExtension";
import { useBoolean } from "@fluentui/react-hooks";
import { useToast } from "../Contexts/ToastContext";
import useAppInsights from "../Hooks/UseAppInsights";
import debounce from "lodash/debounce";
import { NavType, OperationStatus } from "../Shared/AppInsights";
import useLocalStorageState from "../Hooks/useLocalStorageStage";
import ArchiveExperimentConfirmationModal from "./ArchiveExperimentConfirmationModal/ArchiveExperimentConfirmationModal";
import { renderMultiColumnDetailsHeader } from "./MultiColumnHeader";
import FilterTotalsComponent from "../Shared/FilterComponent/FilterTotalsComponent";
import { useDataAccessContext } from "../Contexts/DataAccessContext";

interface ExperimentsTableProps {
  /**
   * List of experiments to display in the table
   */
  experiments: ExperimentType[];
  /**
   * Boolean indicating whether the experiments are currently being loaded, used to show loading state in the UI
   */
  loadingExperiments: boolean;
  /**
   * Function to trigger a refresh of the experiments list, used after actions like archiving or cloning an experiment to show the updated list
   */
  refresh: () => void;
  /**
   * When true will direct users to select an extension.
   */
  isRoot?: boolean;
}

/**
 * Component for displaying a table of experiments with filtering, sorting, and actions like clone and archive
 */
const ExperimentsTable: React.FC<ExperimentsTableProps> = ({
  experiments,
  loadingExperiments,
  refresh,
  isRoot,
}) => {
  const { showToast } = useToast();
  const { extensions, setError } = useAppContext();
  const { hasAdoAccess } = useDataAccessContext();
  const { extensionName = "" } = useEv2ExperimentParams();
  const { trackNavigationEvent, trackOperationEvent } =
    useAppInsights("ExperimentsTable");
  const [appliedFilters, setAppliedFilters] = useLocalStorageState<{
    [key: string]: string[];
  }>(`appliedFilters-${extensionName}`, {});
  const [searchString, setSearchString] = useLocalStorageState<string>(
    `searchString-${extensionName}`,
    "",
  );
  const { value: runPipelineStatuses = {}, loading: loadingPipelineStatuses } =
    useFetchEv2RunStatuses(setError);
  const { navigateToExperiment: navigateExperiment } =
    useNavigateEv2Experiments();

  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [isSortDescending, setIsSortDescending] = useState<boolean>(false);
  const [hideArchiveDialog, { toggle: toggleHideArchiveDialog }] =
    useBoolean(true);
  const [experimentToArchive, setExperimentToArchive] =
    useState<ExperimentType | null>(null);

  const extensionExists = useMemo(
    () =>
      !!extensionName && extensions.some((ext) => ext.name === extensionName),
    [extensionName, extensions],
  );

  const trackSearchTelemetry = useMemo(
    () =>
      debounce((value: string) => {
        trackOperationEvent({
          function: "onSearchChange",
          operation: "Search",
          properties: { searchString: value },
        });
      }, 500),
    [trackOperationEvent],
  );

  const handleClone = (item: ExperimentType) => {
    const destination = navigateExperiment(extensionName, item.featureKey, {
      isClone: true,
    });
    trackNavigationEvent({
      destination: destination,
      navType: NavType.Internal,
      function: "handleClone",
      properties: { item },
    });
  };

  const handleArchive = (item: ExperimentType) => {
    toggleHideArchiveDialog();
    setExperimentToArchive(item);
    trackOperationEvent({
      operation: "OpenArchiveDialog",
      function: "handleArchive",
      properties: { item },
    });
  };

  const onArchiveSuccess = (item: ExperimentType) => {
    showToast({
      message: `Archive experiment pull request created successfully for feature "${item.featureKey}"`,
      type: "success",
      duration: 5000,
      dismissible: true,
    });
    refresh();
  };

  const onArchiveCanceled = () => {
    toggleHideArchiveDialog();
    setExperimentToArchive(null);
    trackOperationEvent({
      function: "onArchiveCanceled",
      operation: "ArchiveExperiment",
      status: OperationStatus.Canceled,
    });
  };

  const handleColumnClick = createColumnClickHandler(
    setSortColumn,
    setIsSortDescending,
    sortColumn,
    isSortDescending,
  );

  const columns = getExperimentsTableColumns({
    runPipelineStatuses,
    loadingPipelineStatuses,
    handleClone,
    handleArchive,
    sortState: { sortColumn, isSortDescending },
    hasAdoAccess,
    onColumnClick: (ev: React.MouseEvent<HTMLElement>, column: IColumn) => {
      handleColumnClick(ev, column);
      trackOperationEvent({
        function: "onColumnClick",
        operation: "Sort",
        properties: { columnKey: column.key, isDescending: isSortDescending },
      });
    },
  });

  // Calculate the minimum width of the table based on the columns
  // This is used to set the width of the table so the table will correctly apply horizontal scrolling when there are many columns or when the screen is small
  const tableMinWidth = useMemo(
    () =>
      columns.reduce((totalWidth, column) => totalWidth + column.minWidth, 0),
    [columns],
  );

  // Apply filtering
  const filteredItems = useTableFiltering(experiments, {
    searchString,
    searchFields: ["featureName", "featureDescription"],
    appliedFilters,
    customFilters: {
      status: customStatusFilter(runPipelineStatuses),
      lastModifiedDate: customLastModifiedDateFilter,
      duration: customDurationFilter(runPipelineStatuses),
    },
  });

  // Apply sorting
  const items = useTableSorting(filteredItems, {
    sortColumn,
    isSortDescending,
  });

  const filters = useMemo(
    () => getFilters(experiments, appliedFilters, setAppliedFilters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [experiments, appliedFilters],
  );

  const hasNoResults = items.length === 0 && !loadingExperiments;
  const hasNoExperiments =
    !!extensionName && !loadingExperiments && !experiments?.length;

  if (hasNoExperiments) {
    return (
      <ExperimentsTableNoExperiments extension={extensionName || "unknown"} />
    );
  }

  if (!isRoot && !extensionExists) {
    return <NoEV2Experiments />;
  }

  return (
    <Stack
      tokens={{ childrenGap: 10 }}
      className={styles.experimentTableContainer}
    >
      <FilterComponent
        searchOptions={{
          placeholder: "Search experiment name and description",
          value: searchString,
          onChange: (_, newValue = "") => {
            setSearchString(newValue);
            trackSearchTelemetry(newValue);
          },
        }}
        filters={filters}
        onClearFilters={() => {
          setAppliedFilters({});
          trackOperationEvent({
            function: "onClearFilters",
            operation: "ClearFilters",
            properties: { appliedFilters },
          });
        }}
      />
      <Stack
        className={mergeStyles(sharedStyles.detailsListWrapperWithStickyHeader)}
      >
        <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
          <Stack style={{ width: `max(100%, ${tableMinWidth}px)` }}>
            <ShimmeredDetailsList
              items={items}
              columns={columns}
              compact
              className={mergeStyles(styles.experimentsTable)}
              selectionMode={SelectionMode.none}
              getKey={(item) =>
                item?.key || item?.__fullpath || item?.featureKey
              }
              layoutMode={DetailsListLayoutMode.justified}
              enableShimmer={loadingExperiments}
              onRenderDetailsHeader={(
                props: IDetailsHeaderProps | undefined,
                defaultRender,
              ) => (
                <Sticky stickyPosition={StickyPositionType.Header}>
                  {renderMultiColumnDetailsHeader(props, defaultRender)}
                </Sticky>
              )}
            />
          </Stack>
          <Stack tokens={{ childrenGap: 10 }}>
            {isRoot ? (
              <ExperimentTableSelectExtension
                extensionName={extensionName}
                extensions={extensions}
              />
            ) : (
              hasNoResults && (
                <Stack>
                  <Text>No results...</Text>
                </Stack>
              )
            )}
          </Stack>
        </ScrollablePane>
      </Stack>
      {!hideArchiveDialog && experimentToArchive && (
        <ArchiveExperimentConfirmationModal
          experiment={experimentToArchive}
          onSuccess={onArchiveSuccess}
          onCancel={onArchiveCanceled}
        />
      )}
      <FilterTotalsComponent
        totalItemsCount={experiments.length}
        filteredItemsCount={filteredItems.length}
        appliedFilters={appliedFilters}
        searchString={searchString}
      />
    </Stack>
  );
};

export default ExperimentsTable;
